// Debug interceptor — development and QA only.
// Uses dynamic import() instead of require() for Vite ESM compatibility.
// process.env.NODE_ENV works in both CRA (webpack) and Vite (via define config).
if (process.env.NODE_ENV !== 'production') {
  import('./debugInterceptor').then(({ setupDebugInterceptor }) => {
    import('./services/apiClient').then((mod) => {
      setupDebugInterceptor(mod.default);
    });
  });
}

// Perspective mode bootstrap — only activates inside an iframe (admin
// "View as Customer"). Skip the import entirely for normal page loads
// to keep it out of the main bundle (~277 lines + apiClient dependency).
if (window !== window.parent) {
  import('./services/perspectiveBootstrap').then(m => m.initPerspectiveMode());
}

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import { useApiConfigInit } from './hooks/useApiConfigInit';
import { ToastProvider } from './components/feedback/Toast';
import ScrollToTop from './components/ScrollToTop';
import { LanguageProvider, getLangFromPath, isPublicPage } from './contexts/LanguageContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
const PerspectiveBanner = lazy(() => import('./components/PerspectiveBanner'));
import ErrorBoundary from './components/ErrorBoundary';
import { loadEnNamespaces } from './i18n';
import type { EnGroup } from './i18n/messages/en';

import './App.css';

// Non-critical component styles — forms, modals, tables, cards, admin styles.
// Dynamically imported so Vite produces a separate CSS chunk (~13 KiB) that
// loads async, reducing the render-blocking CSS from ~31 KiB to ~21 KiB.
// This causes a small CLS (~0.06) which is within the "good" threshold (<0.1).
import('./components.css');

// ── Deferred initialization ─────────────────────────────────────────────────
// Error tracking (~464 lines) and analytics (~398 lines) are not needed for
// first paint. Loading them via dynamic import + requestIdleCallback keeps
// them out of the main chunk and defers parse/execution until after the
// browser has rendered the first frame.
const deferInit = (fn: () => void) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fn);
  } else {
    setTimeout(fn, 2000);
  }
};
deferInit(() => import('./services/errorTrackingService').then(m => m.initErrorTracking()));
deferInit(() => import('./services/analyticsService').then(m => m.initAnalytics()));

// ─────────────────────────────────────────────────────────────────────────────
// EAGER — LandingPage stays in the main bundle. At 7.83 KiB (Vite output),
// the lazy-loading overhead (sequential network hop on slow 4G ≈ 1.3s) far
// exceeds the bundle size cost. react-snap pre-renders the HTML regardless.
// NotFoundPage is tiny and used by the catch-all route.
// ─────────────────────────────────────────────────────────────────────────────
import LandingPage  from './customer/pages/LandingPage';
import NotFoundPage from './NotFoundPage';

const LoginPageWithOAuth    = lazy(() =>
	import('./customer/pages/auth/LoginPage').then(m => ({ default: m.LoginPageWithOAuth }))
);
const RegisterPageWithOAuth = lazy(() =>
	import('./customer/pages/auth/RegisterPage').then(m => ({ default: m.RegisterPageWithOAuth }))
);
const ForgotPasswordPage = lazy(() => import('./customer/pages/auth/ForgotPasswordPage'));
const VerifyEmailPage    = lazy(() => import('./customer/pages/auth/VerifyEmailPage'));

// ─────────────────────────────────────────────────────────────────────────────
// LAZY HELPER
// ─────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyFactory<T> = () => Promise<{ default: T }>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withNS<T extends React.ComponentType<any>>(
	pageFactory: LazyFactory<T>,
	...groups: EnGroup[]
): React.LazyExoticComponent<T> {
	return lazy(() =>
		Promise.all([pageFactory(), ...groups.map(g => loadEnNamespaces(g))])
			.then(([mod]) => mod as { default: T })
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// LAZY PAGES
// ─────────────────────────────────────────────────────────────────────────────

// Public marketing
const LearnMore             = withNS(() => import('./customer/pages/LearnMore'),             'marketing');
const FaqPage               = withNS(() => import('./customer/pages/FaqPage'),               'marketing');
const FaqDetailPage         = withNS(() => import('./customer/pages/FaqDetailPage'),         'marketing');
const AppraiserLocatorPage  = withNS(() => import('./customer/pages/AppraiserLocatorPage'),  'marketing');
const SecurityPrivacyPage   = withNS(() => import('./customer/pages/SecurityPrivacyPage'),   'marketing');
const FeaturesPage          = withNS(() => import('./customer/pages/FeaturesPage'),          'marketing');
const DelegationFeaturePage = withNS(() => import('./customer/pages/DelegationFeaturePage'), 'marketing', 'app');
const CoApplicantFeaturePage = withNS(() => import('./customer/pages/CoApplicantFeaturePage'), 'marketing', 'app');
const ManageDelegatesPage   = withNS(() => import('./customer/pages/ManageDelegatesPage'),   'app');
const DelegationAcceptPage  = withNS(() => import('./customer/pages/DelegationAcceptPage'),  'app');
const PricingPage           = withNS(() => import('./customer/pages/PricingPage'),           'marketing');
const PremiumSubmissionPage = withNS(() => import('./customer/pages/PremiumSubmissionPage'), 'marketing');

// Company
const AboutPage        = withNS(() => import('./customer/pages/company/AboutPage'),        'marketing');
const ContactPage      = withNS(() => import('./customer/pages/company/ContactPage'),      'marketing');
const BookMeetingPage  = withNS(() => import('./customer/pages/BookMeetingPage'),          'marketing');
const InvitePage       = withNS(() => import('./customer/pages/InvitePage'),               'marketing');
const BlogPage         = withNS(() => import('./customer/pages/company/BlogPage'),         'marketing');
const WhyNotBrokerPage = withNS(() => import('./customer/pages/company/WhyNotBrokerPage'), 'marketing');

// Sandbox (no i18n — English-only investor evaluation tools)
const SandboxPage = lazy(() => import('./customer/pages/SandboxPage'));
const SandboxCanadaPage = lazy(() => import('./customer/pages/SandboxCanadaPage'));
const SandboxUSAPage = lazy(() => import('./customer/pages/SandboxUSAPage'));
const SandboxMexicoPage = lazy(() => import('./customer/pages/SandboxMexicoPage'));
const SandboxPanamaPage = lazy(() => import('./customer/pages/SandboxPanamaPage'));
const SandboxCostaRicaPage = lazy(() => import('./customer/pages/SandboxCostaRicaPage'));

// Legal
const PrivacyPolicy  = withNS(() => import('./customer/pages/legal/PrivacyPolicy'),  'legal');
const TermsOfService = withNS(() => import('./customer/pages/legal/TermsOfService'), 'legal');
const Compliance     = withNS(() => import('./customer/pages/legal/Compliance'),     'legal');
const SecurityPolicy = withNS(() => import('./customer/pages/legal/SecurityPolicy'), 'legal');

// Blog routes — lazy-loaded via wildcard route (keeps blogRegistry out of main bundle)
const BlogRouter     = lazy(() => import('./config/BlogRouter'));
const LangBlogRouter = lazy(() => import('./config/BlogRouter').then(m => ({ default: m.LangBlogRouter })));

// Customer dashboard
const DashboardPage = withNS(() => import('./customer/pages/dashboard/DashboardPage'), 'app');
const DocumentsPage = withNS(() => import('./customer/pages/dashboard/DocumentsPage'), 'app');
const ProfilePage   = withNS(() => import('./customer/pages/dashboard/ProfilePage'),   'app');
const MaskedDocumentPreviewPage = withNS(() => import('./customer/pages/dashboard/MaskedDocumentPreviewPage'), 'app');
const DocumentConditionsPage    = lazy(() => import('./customer/pages/dashboard/DocumentConditionsPage'));

// Application flow
const ApplicationLayout         = withNS(() => import('./customer/pages/application/ApplicationLayout'),         'apply', 'app');
const Step1CustomerType         = withNS(() => import('./customer/pages/application/Step1CustomerType'),         'apply');
const Step2ProductType          = withNS(() => import('./customer/pages/application/Step2ProductType'),          'apply');
const Step2_5BankAccounts       = withNS(() => import('./customer/pages/application/Step2_5BankAccounts'),       'apply');
const Step3PersonalInfo         = withNS(() => import('./customer/pages/application/Step3PersonalInfo'),         'apply');
const Step4FinancialInfo        = withNS(() => import('./customer/pages/application/Step4FinancialInfo'),        'apply');
const Step5PropertyInfo         = withNS(() => import('./customer/pages/application/Step5PropertyInfo'),         'apply');
const Step5_5AppraiserSelection = withNS(() => import('./customer/pages/application/Step5_5AppraiserSelection'), 'apply');
const Step6Documents            = withNS(() => import('./customer/pages/application/Step6Documents'),            'apply');
const CreditAssessment          = withNS(() => import('./customer/pages/application/CreditAssessment'),          'apply');
const PremiumSubmission         = withNS(() => import('./customer/pages/application/PremiumSubmission'),         'apply');
const Step7Review               = withNS(() => import('./customer/pages/application/Step7Review'),               'apply');
const SecureDataEntry           = withNS(() => import('./customer/pages/application/SecureDataEntry'),           'apply');
const Confirmation              = withNS(() => import('./customer/pages/application/Confirmation'),              'apply');
const UploadDocumentsPage       = withNS(() => import('./customer/pages/application/UploadDocumentsPage'),       'apply');
const CoApplicantPortal         = withNS(() => import('./customer/pages/application/CoApplicantPortal'),         'apply', 'app');

// Pre-construction flow (Phases 10–11)
const Step5PreConstructionContext = withNS(() => import('./customer/pages/application/Step5PreConstructionContext'), 'apply');
const EquityMortgageFlow         = withNS(() => import('./customer/pages/application/EquityMortgageFlow'),         'apply');
const BuilderQuestionnairePage   = withNS(() => import('./customer/pages/application/BuilderQuestionnairePage'),   'apply');
const LifeChangeReportPage       = withNS(() => import('./customer/pages/application/LifeChangeReportPage'),       'apply', 'app');
const ReprocessingTriggerPage    = withNS(() => import('./customer/pages/application/ReprocessingTriggerPage'),    'apply', 'app');
const LtvComparisonPanel         = withNS(() => import('./customer/pages/dashboard/LtvComparisonPanel'),           'app');

// Bank portal
const BankDocumentDownload   = lazy(() => import('./bank/pages/BankDocumentDownload'));
const BankDossierDownload    = lazy(() => import('./bank/pages/BankDossierDownload'));
const LetterVerificationPage = lazy(() => import('./bank/pages/LetterVerificationPage'));

// Admin
const AdminLayout         = lazy(() => import('./admin/components/AdminLayout'));
const AdminDashboard      = lazy(() => import('./admin/pages/AdminDashboard'));
const ApplicationsList    = lazy(() => import('./admin/pages/ApplicationsList'));
const APIExplorer         = lazy(() => import('./admin/pages/APIExplorer'));
const DatabaseInspector   = lazy(() => import('./admin/pages/DatabaseInspector'));
const EmailHub            = lazy(() => import('./admin/pages/EmailHub'));
const UserManagement      = lazy(() => import('./admin/pages/UserManagement'));
const BankManagement      = lazy(() => import('./admin/pages/BankManagement'));
const EmailRouting        = lazy(() => import('./admin/pages/EmailRouting'));
const ConfigurationStatus = lazy(() => import('./admin/pages/ConfigurationStatus'));
const DocumentRepository  = lazy(() => import('./admin/pages/DocumentRepository'));
const TaskManagement      = lazy(() => import('./admin/pages/TaskManagement'));
const Reports             = lazy(() => import('./admin/pages/Reports'));
const LifecycleTest       = lazy(() => import('./admin/pages/LifecycleTest'));
const UIPageTest          = lazy(() => import('./admin/pages/UIPageTest'));
const StabilityAssessment = lazy(() => import('./admin/pages/StabilityAssessment'));
const SentinelPage        = lazy(() => import('./admin/pages/SentinelPage'));
const UnderwriterTests    = withNS(() => import('./admin/pages/UnderwriterTests'), 'app');
const DuStatus            = withNS(() => import('./admin/pages/DuStatus'), 'app');
const PerspectiveView     = lazy(() => import('./admin/pages/PerspectiveView'));
const AdminProfilePage    = lazy(() => import('./admin/pages/AdminProfilePage'));

// Pre-construction admin pages (Phase 12)
const AdminBuilderDashboard       = lazy(() => import('./admin/pages/AdminBuilderDashboard'));
const AdminRevenueDashboard       = lazy(() => import('./admin/pages/AdminRevenueDashboard'));
const AdminPreConstructionPipeline = lazy(() => import('./admin/pages/AdminPreConstructionPipeline'));

// Monitoring (Grafana + Prometheus iframes)
const GrafanaDashboard    = lazy(() => import('./admin/pages/GrafanaDashboard'));
const PrometheusDashboard = lazy(() => import('./admin/pages/PrometheusDashboard'));

// Public builder profiles & portal (Phase 13)
const PublicBuilderProfilePage = withNS(() => import('./customer/pages/PublicBuilderProfilePage'), 'marketing');
const PublicProjectProfilePage = withNS(() => import('./customer/pages/PublicProjectProfilePage'), 'marketing');
const BuilderClaimPage         = withNS(() => import('./customer/pages/BuilderClaimPage'),         'marketing');
const BuilderPortalPage        = withNS(() => import('./customer/pages/BuilderPortalPage'),        'apply', 'marketing');

// Simulator — admin (internal) versions
const SimulatorPage       = withNS(() => import('./admin/pages/simulator/SimulatorPage'),       'simulator');
const SimulatorBankLocked = withNS(() => import('./admin/pages/simulator/SimulatorBankLocked'), 'simulator');

// Simulator — public consumer-facing version
const PublicSimulatorPage = withNS(() => import('./customer/pages/simulator/PublicSimulatorPage'), 'simulator', 'apply');
const PublicEquitySimulatorPage = withNS(() => import('./customer/pages/simulator/PublicEquitySimulatorPage'), 'apply');
const FillGatewayPage     = withNS(() => import('./customer/pages/FillGatewayPage'), 'simulator', 'apply', 'fill');
const ApplyGatewayPage    = lazy(() => import('./customer/pages/ApplyGatewayPage'));

// Calculator
const CalculatorPage = withNS(() => import('./customer/pages/calculator/CalculatorPage'), 'simulator', 'legal');

// Diligence
const DiligenceLayout = withNS(() => import('./diligence/DiligenceLayout'),      'diligence');
const OverviewPage    = withNS(() => import('./diligence/pages/OverviewPage'),   'diligence');
const BuyersPage      = withNS(() => import('./diligence/pages/BuyersPage'),     'diligence');
const SellersPage     = withNS(() => import('./diligence/pages/SellersPage'),    'diligence');
const BanksPage       = withNS(() => import('./diligence/pages/BanksPage'),      'diligence');
const AgentsPage      = withNS(() => import('./diligence/pages/AgentsPage'),     'diligence');
const BuildersPage    = withNS(() => import('./diligence/pages/BuildersPage'),   'diligence');
const RegulatorsPage  = withNS(() => import('./diligence/pages/RegulatorsPage'), 'diligence');
const LegalPage       = lazy(() => import('./diligence/pages/LegalPage'));
const AppraisersPage  = withNS(() => import('./diligence/pages/AppraisersPage'), 'diligence');
const ITSecurityPage  = withNS(() => import('./diligence/pages/ITSecurityPage'), 'diligence');
const InvestorsPage   = withNS(() => import('./diligence/pages/InvestorsPage'),  'diligence');

// Dev/examples
const StylesShowcase     = lazy(() => import('./examples/StylesShowcase'));
const ComponentsShowcase = lazy(() => import('./examples/ComponentsShowcase'));

// System
const SystemVersionPage = lazy(() => import('./app/system/version/page'));

// ─────────────────────────────────────────────────────────────────────────────

function PageLoader() {
	return (
		<div style={{
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif',
		}}>
			<div style={{ textAlign: 'center' }}>
				<div style={{ fontSize: '24px', marginBottom: '16px' }}>⚙️</div>
				<div>Loading...</div>
			</div>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// LangSync
//
// Rendered inside every /:lang/* route. Reads :lang and syncs it into
// LanguageContext so all child components see the correct language.
//
// ⚠️  The /:lang routes MUST be declared last (just before the catch-all).
//     /:lang is a wildcard matching any single path segment — if placed before
//     /admin, /customer, /application, etc., those routes would never match.
// ─────────────────────────────────────────────────────────────────────────────

/** Redirect /register → /customer/register, preserving query params (e.g. delegation_token). */
function RegisterRedirect() {
    const location = useLocation();
    return <Navigate to={`/customer/register${location.search}`} replace />;
}

function LangSync({ children }: { children: React.ReactNode }) {
	const { lang } = useParams<{ lang?: string }>();
	const { language, setLanguage } = useLanguage();
	const isValidLang = lang === 'es' || lang === 'fr';

	// Hooks must always run unconditionally — guard applied after.
	React.useEffect(() => {
		if (isValidLang && language !== lang) {
			setLanguage(lang as 'es' | 'fr');
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lang, isValidLang]);

	if (!isValidLang) return <NotFoundPage />;
	return <>{children}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
// LocalePrefixEnforcer
//
// Watches every navigation. If the user's language is ES or FR but the URL
// has no locale prefix, redirects transparently — no <Link> needs changing.
//
// Uses window.location.replace (NOT React Router navigate) to avoid any
// interference with the React Router state machine during admin transitions.
// ─────────────────────────────────────────────────────────────────────────────

function LocalePrefixEnforcer() {
	const location   = useLocation();
	const { language } = useLanguage();

	React.useEffect(() => {
		if (language === 'en') return;
		if (location.pathname.startsWith('/admin')) return;  // explicit admin guard
		if (!isPublicPage(location.pathname)) return;
		const existingLang = getLangFromPath(location.pathname);
		if (existingLang) return;
		const suffix = location.pathname === '/' ? '' : location.pathname;
		window.location.replace(`/${language}${suffix}${location.search}`);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, language]);

	return null;
}

function App() {
	const { isLoading, error } = useApiConfigInit();

	React.useEffect(() => {
		if (window.location.hostname === 'www.hipotech.net') {
			window.location.replace(window.location.href.replace('://www.', '://'));
		}
	}, []);

	if (isLoading) {
		return (
			<div style={{
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif',
			}}>
				<div style={{ textAlign: 'center' }}>
					<div style={{ fontSize: '24px', marginBottom: '16px' }}>⚙️</div>
					<div>Loading configuration...</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div style={{
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif',
			}}>
				<div style={{ textAlign: 'center', padding: '32px', maxWidth: '500px' }}>
					<div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
					<h2 style={{ marginBottom: '8px' }}>Configuration Error</h2>
					<p style={{ color: '#666' }}>{error.message}</p>
					<button
						onClick={() => window.location.reload()}
						style={{ marginTop: '16px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' }}
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<ErrorBoundary componentName="App">
		<LanguageProvider>
			<FeatureFlagProvider>
			<ToastProvider position="bottom-right">
				<Router>
					<ScrollToTop />
					<LocalePrefixEnforcer />
					<Suspense fallback={null}><PerspectiveBanner /></Suspense>
					<Suspense fallback={<PageLoader />}>
						<Routes>

							{/* ── Public marketing (English, no prefix) ──────────────────── */}
							<Route path="/"                   element={<LandingPage />} />
							<Route path="/learn-more"         element={<LearnMore />} />
							<Route path="/book"               element={<BookMeetingPage />} />
							<Route path="/faq"                element={<FaqPage />} />
							<Route path="/faq/:slug"          element={<FaqDetailPage />} />
							<Route path="/appraisers"         element={<AppraiserLocatorPage />} />
							<Route path="/tasadores"          element={<Navigate to="/appraisers" replace />} />
							<Route path="/evaluateurs"        element={<Navigate to="/appraisers" replace />} />
							<Route path="/security"           element={<SecurityPrivacyPage />} />
							<Route path="/features"           element={<FeaturesPage />} />
							<Route path="/features/delegation" element={<DelegationFeaturePage />} />
						<Route path="/features/co-applicant" element={<CoApplicantFeaturePage />} />
							<Route path="/pricing"            element={<PricingPage />} />
							<Route path="/premium-submission" element={<PremiumSubmissionPage />} />
							<Route path="/about"              element={<AboutPage />} />
							<Route path="/why-not-a-broker"   element={<WhyNotBrokerPage />} />
							<Route path="/contact"            element={<ContactPage />} />
							<Route path="/invite"             element={<InvitePage />} />
							<Route path="/invitation/accept"  element={<CoApplicantPortal />} />

							{/* ── Sandbox (investor evaluation) ────────────────────────── */}
							<Route path="/explore/platform"              element={<SandboxPage />} />
							<Route path="/explore/platform/canada"       element={<SandboxCanadaPage />} />
							<Route path="/explore/platform/usa"          element={<SandboxUSAPage />} />
							<Route path="/explore/platform/mexico"       element={<SandboxMexicoPage />} />
							<Route path="/explore/platform/panama"       element={<SandboxPanamaPage />} />
							<Route path="/explore/platform/costa-rica"   element={<SandboxCostaRicaPage />} />

							{/* ── Blog ───────────────────────────────────────────────────── */}
							<Route path="/blog"                            element={<BlogPage />} />
							<Route path="/blog/:slug"                      element={<BlogRouter />} />

							{/* ── Legal ──────────────────────────────────────────────────── */}
							<Route path="/privacy-policy"   element={<PrivacyPolicy />} />
							<Route path="/terms-of-service" element={<TermsOfService />} />
							<Route path="/compliance"       element={<Compliance />} />
							<Route path="/security-policy"  element={<SecurityPolicy />} />

							{/* ── Bank portal ────────────────────────────────────────────── */}
							<Route path="/bank/documents/:bankApplicationId" element={<BankDocumentDownload />} />
							<Route path="/bank/dossier/:accreditationId"     element={<BankDossierDownload />} />
							<Route path="/verify/letter/:bankApplicationId"  element={<LetterVerificationPage />} />

							{/* ── Public builder & project profiles (Phase 13) ──────────── */}
							<Route path="/builders/:slug"            element={<PublicBuilderProfilePage />} />
							<Route path="/builders/:slug/claim"      element={<BuilderClaimPage />} />
							<Route path="/projects/:slug"            element={<PublicProjectProfilePage />} />
							<Route path="/builder-portal/:builderId" element={<ProtectedRoute allowedRoles={['BUILDER', 'ADMIN']}><BuilderPortalPage /></ProtectedRoute>} />

							{/* ── Admin ──────────────────────────────────────────────────── */}
							<Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'REVIEWER']} />}>
								<Route element={<AdminLayout />}>
									<Route index element={<Navigate to="/admin/dashboard" replace />} />
									<Route path="dashboard"            element={<AdminDashboard />} />
									<Route path="applications"         element={<ApplicationsList />} />
									<Route path="api-explorer"         element={<APIExplorer />} />
									<Route path="database"             element={<DatabaseInspector />} />
									<Route path="users"                element={<UserManagement />} />
									<Route path="banks"                element={<BankManagement />} />
									<Route path="banks/email-routing"  element={<EmailRouting />} />
									<Route path="documents"            element={<DocumentRepository />} />
									<Route path="tasks"                element={<TaskManagement />} />
									<Route path="reports"              element={<Reports />} />
									<Route path="configuration-status" element={<ConfigurationStatus />} />
									<Route path="lifecycle-test"       element={<LifecycleTest />} />
									<Route path="underwriter-tests"    element={<UnderwriterTests />} />
									<Route path="du-status"            element={<DuStatus />} />
									<Route path="ui-test"              element={<UIPageTest />} />
									<Route path="stability"            element={<StabilityAssessment />} />
									<Route path="sentinel"             element={<SentinelPage />} />
									<Route path="perspective"          element={<PerspectiveView />} />
									<Route path="profile"              element={<AdminProfilePage />} />
									<Route path="builders"             element={<AdminBuilderDashboard />} />
									<Route path="revenue"              element={<AdminRevenueDashboard />} />
									<Route path="preconstruction"      element={<AdminPreConstructionPipeline />} />
									<Route path="grafana"              element={<GrafanaDashboard />} />
									<Route path="prometheus"           element={<PrometheusDashboard />} />
								</Route>
								<Route path="/admin/database" element={<DatabaseInspector />} />
								<Route path="/admin/api"      element={<APIExplorer />} />
							</Route>

							{/* ── Dev / internal ─────────────────────────────────────────── */}
							<Route path="/styles"     element={<StylesShowcase />} />
							<Route path="/components" element={<ComponentsShowcase />} />
							<Route path="/api-explorer-standalone" element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
								<Route index element={<APIExplorer />} />
							</Route>
							<Route path="/database-standalone" element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
								<Route index element={<DatabaseInspector />} />
							</Route>

							{/* ── Diligence ──────────────────────────────────────────────── */}
							<Route path="/diligence" element={<DiligenceLayout />}>
								<Route index element={<Navigate to="/diligence/overview" replace />} />
								<Route path="overview"   element={<OverviewPage />} />
								<Route path="buyers"     element={<BuyersPage />} />
								<Route path="sellers"    element={<SellersPage />} />
								<Route path="banks"      element={<BanksPage />} />
								<Route path="agents"     element={<AgentsPage />} />
								<Route path="builders"   element={<BuildersPage />} />
								<Route path="regulators" element={<RegulatorsPage />} />
								<Route path="legal"      element={<LegalPage />} />
								<Route path="appraisers" element={<AppraisersPage />} />
								<Route path="it"         element={<ITSecurityPage />} />
								<Route path="investors"  element={<InvestorsPage />} />
							</Route>

							{/* ── Auth ───────────────────────────────────────────────────── */}
							<Route path="/customer/login"           element={<LoginPageWithOAuth />} />
							<Route path="/customer/register"        element={<RegisterPageWithOAuth />} />
							<Route path="/customer/verify-email"    element={<VerifyEmailPage />} />
							<Route path="/verify-email"             element={<VerifyEmailPage />} />
							<Route path="/customer/forgot-password" element={<ForgotPasswordPage />} />

							{/* ── Customer dashboard ─────────────────────────────────────── */}
							<Route path="/customer/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
							<Route path="/customer/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
							<Route path="/customer/documents/:id/masked-preview" element={<ProtectedRoute><MaskedDocumentPreviewPage /></ProtectedRoute>} />
							<Route path="/customer/applications/:id/conditions" element={<ProtectedRoute><DocumentConditionsPage /></ProtectedRoute>} />
							<Route path="/customer/profile"   element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
							<Route path="/settings/delegates" element={<ProtectedRoute><ManageDelegatesPage /></ProtectedRoute>} />
							<Route path="/delegation/accept" element={<ProtectedRoute><DelegationAcceptPage /></ProtectedRoute>} />

							{/* ── Co-applicant ───────────────────────────────────────────── */}
							<Route path="/co-applicant/accept"                  element={<CoApplicantPortal />} />
							<Route path="/co-applicant/decline"                 element={<CoApplicantPortal />} />
							<Route path="/customer/co-applicant/accept"         element={<CoApplicantPortal />} />
							<Route path="/customer/co-applicant/decline"        element={<CoApplicantPortal />} />
							<Route path="/co-applicant"                         element={<ProtectedRoute><CoApplicantPortal /></ProtectedRoute>} />
							<Route path="/co-applicant/:participantId"          element={<ProtectedRoute><CoApplicantPortal /></ProtectedRoute>} />
							<Route path="/customer/co-applicant"                element={<ProtectedRoute><CoApplicantPortal /></ProtectedRoute>} />
							<Route path="/customer/co-applicant/:participantId" element={<ProtectedRoute><CoApplicantPortal /></ProtectedRoute>} />

							{/* ── Application flow ───────────────────────────────────────── */}
							<Route path="/application/:applicationId" element={<ProtectedRoute><ApplicationLayout /></ProtectedRoute>}>
								<Route path="step-1"   element={<Step1CustomerType />} />
								<Route path="step-2"   element={<Step2ProductType />} />
								<Route path="step-2.5" element={<Step2_5BankAccounts />} />
								<Route path="step-3"   element={<Step3PersonalInfo />} />
								<Route path="step-4"   element={<Step4FinancialInfo />} />
								<Route path="step-5"       element={<Step5PropertyInfo />} />
								<Route path="step-5-precon" element={<Step5PreConstructionContext />} />
								<Route path="step-equity"   element={<EquityMortgageFlow />} />
								<Route path="step-5.5" element={<Step5_5AppraiserSelection />} />
								<Route path="step-6"   element={<Step6Documents />} />
								<Route path="credit"   element={<CreditAssessment />} />
								<Route path="premium"  element={<PremiumSubmission />} />
								<Route path="review"   element={<Step7Review />} />
							</Route>
							<Route path="/application/:id/secure-data"      element={<ProtectedRoute><SecureDataEntry /></ProtectedRoute>} />
							<Route path="/application/:id/confirmation"     element={<ProtectedRoute><Confirmation /></ProtectedRoute>} />
							<Route path="/application/:id/upload-documents" element={<ProtectedRoute><UploadDocumentsPage /></ProtectedRoute>} />

						{/* ── Pre-construction flows (Phases 10–11) ──────────────────── */}
						<Route path="/applications/:applicationId/builder-questionnaire" element={<ProtectedRoute><BuilderQuestionnairePage /></ProtectedRoute>} />
						<Route path="/applications/:applicationId/life-change"           element={<ProtectedRoute><LifeChangeReportPage /></ProtectedRoute>} />
						<Route path="/applications/:applicationId/reprocess"             element={<ProtectedRoute><ReprocessingTriggerPage /></ProtectedRoute>} />
						<Route path="/applications/:applicationId/ltv-analysis"          element={<ProtectedRoute><LtvComparisonPanel /></ProtectedRoute>} />

							{/* ── Legacy redirects ───────────────────────────────────────── */}
							<Route path="/customer/apply" element={<Navigate to="/customer/register" replace />} />
							<Route path="/register" element={<RegisterRedirect />} />

							{/* ── System / tools ─────────────────────────────────────────── */}
							<Route path="/system/version"      element={<SystemVersionPage />} />
							{/* Public consumer simulator — no auth required */}
							<Route path="/simulator"           element={<PublicSimulatorPage />} />
						<Route path="/simulator/equity"    element={<PublicEquitySimulatorPage />} />
							<Route path="/fill"                element={<FillGatewayPage />} />
							<Route path="/apply"               element={<ApplyGatewayPage />} />

							{/* Internal admin simulator — moved from /simulator */}
							<Route path="/admin-simulator"           element={<SimulatorPage />} />
							<Route path="/admin-simulator/:bankSlug" element={<SimulatorBankLocked />} />
							<Route path="/calculator"          element={<CalculatorPage />} />

							{/* ── /es/* and /fr/* locale routes ──────────────────────────── */}
							{/*                                                                */}
							{/* ⚠️  KEEP THIS BLOCK LAST (before catch-all).                  */}
							{/*     /:lang matches ANY single segment — /admin, /customer,     */}
							{/*     /diligence, etc. would all break if this block came first. */}
							<Route path="/:lang"                           element={<LangSync><LandingPage /></LangSync>} />
							<Route path="/:lang/learn-more"                element={<LangSync><LearnMore /></LangSync>} />
							<Route path="/:lang/book"                      element={<LangSync><BookMeetingPage /></LangSync>} />
							<Route path="/:lang/faq"                       element={<LangSync><FaqPage /></LangSync>} />
							<Route path="/:lang/faq/:slug"                 element={<LangSync><FaqDetailPage /></LangSync>} />
							<Route path="/:lang/appraisers"                element={<LangSync><AppraiserLocatorPage /></LangSync>} />
							<Route path="/:lang/security"                  element={<LangSync><SecurityPrivacyPage /></LangSync>} />
							<Route path="/:lang/features"                  element={<LangSync><FeaturesPage /></LangSync>} />
							<Route path="/:lang/features/delegation"       element={<LangSync><DelegationFeaturePage /></LangSync>} />
						<Route path="/:lang/features/co-applicant"     element={<LangSync><CoApplicantFeaturePage /></LangSync>} />
							<Route path="/:lang/pricing"                   element={<LangSync><PricingPage /></LangSync>} />
							<Route path="/:lang/about"                     element={<LangSync><AboutPage /></LangSync>} />
							<Route path="/:lang/why-not-a-broker"          element={<LangSync><WhyNotBrokerPage /></LangSync>} />
							<Route path="/:lang/contact"                   element={<LangSync><ContactPage /></LangSync>} />
							<Route path="/:lang/privacy-policy"            element={<LangSync><PrivacyPolicy /></LangSync>} />
							<Route path="/:lang/terms-of-service"          element={<LangSync><TermsOfService /></LangSync>} />
							<Route path="/:lang/compliance"              element={<LangSync><Compliance /></LangSync>} />
							<Route path="/:lang/security-policy"         element={<LangSync><SecurityPolicy /></LangSync>} />
							<Route path="/:lang/blog"                           element={<LangSync><BlogPage /></LangSync>} />
							<Route path="/:lang/blog/:slug"                     element={<LangSync><LangBlogRouter /></LangSync>} />
							<Route path="/:lang/builders/:slug"       element={<LangSync><PublicBuilderProfilePage /></LangSync>} />
						<Route path="/:lang/builders/:slug/claim" element={<LangSync><BuilderClaimPage /></LangSync>} />
						<Route path="/:lang/projects/:slug"       element={<LangSync><PublicProjectProfilePage /></LangSync>} />
						<Route path="/:lang/simulator"                 element={<LangSync><PublicSimulatorPage /></LangSync>} />
							<Route path="/:lang/fill"                      element={<LangSync><FillGatewayPage /></LangSync>} />
							<Route path="/:lang/apply"                     element={<LangSync><ApplyGatewayPage /></LangSync>} />

							{/* ── Catch-all ──────────────────────────────────────────────── */}
							<Route path="*" element={<NotFoundPage />} />

						</Routes>
					</Suspense>
				</Router>
			</ToastProvider>
			</FeatureFlagProvider>
		</LanguageProvider>
		</ErrorBoundary>
	);
}

export default App;